export type CalendarEvent = {
  id: string;
  type: 'assn' | 'lecture' | 'handout' | 'break' | 'note';
  title: string;
  concepts?: string;
  environment?: 'ide' | string;
  exampleId?: string;
  slidesLink?: string;
  handoutLink?: string;
  start: number;
  duration?: number;
};

export type EventLinks = {
  assignment?: string;
  example?: string;
  solution?: string;
  slides?: string;
  handout?: string;
};

export const parseConcepts = (event: CalendarEvent): string[] => {
  if (!event.concepts || event.concepts.length === 0) return [];
  return event.concepts
    .trim()
    .split(',')
    .map(c => c.trim());
};

export const getEventLinks = (event: CalendarEvent): EventLinks => {
  const links: EventLinks = {};
  if (event.type === 'assn') {
    if (event.environment === 'ide') {
      links.assignment = `/ide/a/${event.id}`;
      if (event.exampleId) {
        links.example = `/ide/a/${event.exampleId}`;
      }
    } else {
      links.assignment = `/g/${event.id}`;
      if (event.exampleId) {
        links.example = `https://github.com/sierrawang/code-for-humanities-assignments/tree/main/assignments/${event.exampleId}`;
      }
    }
  } else if (event.type === 'lecture') {
    links.slides = event.slidesLink;
  } else if (event.type === 'handout') {
    links.handout = event.handoutLink;
  }
  return links;
};

// `hour` is a decimal hour of day (9.5 = 9:30 AM, 13.25 = 1:15 PM).
// Renders as 12-hour clock with AM/PM.
export const formatHour = (hour: number): string => {
  if (typeof hour !== 'number' || !isFinite(hour)) return '';
  let h = Math.floor(hour);
  let m = Math.round((hour - h) * 60);
  if (m === 60) { m = 0; h += 1; }
  const period = h < 12 ? 'AM' : 'PM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${m.toString().padStart(2, '0')} ${period}`;
};

// Round a decimal hour to the nearest 5-minute increment, so approximate
// Firestore times display cleanly.
export const roundToFiveMin = (hour: number): number => {
  if (typeof hour !== 'number' || !isFinite(hour)) return hour;
  return Math.round(hour * 12) / 12;
};

export const conceptColor = (concept: string): string => {
  const str = concept.toLowerCase();
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = hash % 360;
  return `hsl(${hue}, 70%, 90%)`;
};
